import React, { useEffect, useRef } from 'react';
import { fillFrequencyData } from './speechPlaybackBands';

export type VoiceVisualState =
  | 'idle'
  | 'requesting_permission'
  | 'listening'
  | 'speech_detected'
  | 'processing'
  | 'speaking'
  | 'interrupted'
  | 'paused'
  | 'error'
  | 'closed';

const BAR_COUNT = 180;

function ringColor(mix: number): string {
  const stops: Array<[number, number, number, number]> = [
    [0, 0x00, 0x90, 0x53],
    [0.5, 0x2b, 0x92, 0xac],
    [1, 0x02, 0x88, 0xd1],
  ];
  const t = ((mix % 1) + 1) % 1;
  let left = stops[0];
  let right = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i += 1) {
    if (t >= stops[i][0] && t <= stops[i + 1][0]) {
      left = stops[i];
      right = stops[i + 1];
      break;
    }
  }
  const span = right[0] - left[0] || 1;
  const local = (t - left[0]) / span;
  const channel = (from: number, to: number) => Math.round(from + (to - from) * local);
  return `rgb(${channel(left[1], right[1])}, ${channel(left[2], right[2])}, ${channel(left[3], right[3])})`;
}

interface Props {
  state: VoiceVisualState;
  analyser: AnalyserNode | null;
}

export function GreeningVoiceVisualizer({ state, analyser }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const analyserRef = useRef(analyser);
  analyserRef.current = analyser;
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let frame = 0;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const freq = new Uint8Array(256);

    const draw = () => {
      frame = requestAnimationFrame(draw);
      const parent = canvas.parentElement;
      // Tamanho EXIBIDO (CSS px). O buffer do canvas é esse valor × devicePixelRatio
      // e o contexto é escalado pelo DPR: renderiza nítido no DPI nativo de qualquer
      // tela (retina ou não) e mantém a mesma "textura" das barras. Sem isto, o
      // buffer não batia com a exibição (ficava esticado/borrado) e divergia por tela.
      // Cabe na altura: em telas baixas (Windows 125%, zoom, laptops) a onda
      // encolhe para o resto da cápsula (título, avatares, rótulo, botões)
      // continuar visível sem cortar. ~380px = conteúdo fixo + folgas do overlay.
      const heightBudget = Math.max(140, (window.innerHeight || 800) - 380);
      const size = Math.min(parent?.clientWidth || 320, 300, heightBudget);
      const dpr = window.devicePixelRatio || 1;
      const need = Math.round(size * dpr);
      if (canvas.width !== need) {
        canvas.width = need;
        canvas.height = need;
        canvas.style.width = `${size}px`;
        canvas.style.height = `${size}px`;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const visual = stateRef.current;
      if (visual === 'closed') {
        ctx.clearRect(0, 0, size, size);
        return;
      }
      ctx.clearRect(0, 0, size, size);
      const cx = size / 2;
      const cy = size / 2;
      const inner = size * 0.12;
      const maxReach = size / 2 - inner - 6;
      const node = analyserRef.current;
      const micLive = (visual === 'listening' || visual === 'speech_detected') && node;
      const speakLive = visual === 'speaking';
      const live = Boolean(micLive || speakLive);
      if (micLive && node) {
        node.getByteFrequencyData(freq);
      } else if (speakLive) {
        fillFrequencyData(freq);
      }
      const energy = live ? freq.reduce((sum, v) => sum + v, 0) / freq.length / 255 : 0;
      const t = performance.now() / 1000;
      const amp =
        visual === 'paused' || visual === 'error' || reduced
          ? 8
          : visual === 'processing' || visual === 'requesting_permission'
            ? maxReach * 0.4
            : live
              ? maxReach * (0.45 + energy * 0.55)
              : maxReach * 0.9;

      for (let i = 0; i < BAR_COUNT; i += 1) {
        const angle = (i / BAR_COUNT) * Math.PI * 2 - Math.PI / 2;
        // Ritmo próximo da fala (~2,5× o anterior); vale mais baixo chega perto do círculo branco.
        const fluid =
          0.38 +
          0.34 * Math.sin(t * 3.2 + i * 0.04) +
          0.22 * Math.sin(t * 1.9 + i * 0.07) +
          0.16 * Math.sin(t * 4.6 + i * 0.015);
        const shaped = Math.min(1, Math.max(0, fluid));
        const sample = live ? shaped * 0.35 + (freq[i % freq.length] / 255) * 0.65 : shaped;
        const len = Math.min(maxReach, amp * (0.16 + 0.84 * sample));
        const x1 = cx + Math.cos(angle) * inner;
        const y1 = cy + Math.sin(angle) * inner;
        const x2 = cx + Math.cos(angle) * (inner + len);
        const y2 = cy + Math.sin(angle) * (inner + len);
        ctx.strokeStyle = ringColor(i / BAR_COUNT);
        ctx.lineWidth = Math.max(1.2, size / 220);
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, []);

  // O tamanho em px (e o DPR) é definido no loop de desenho; aqui só centraliza e
  // impede overflow em telas estreitas. Nada de width:100% esticando o buffer.
  return <canvas ref={canvasRef} aria-hidden style={{ display: 'block', margin: '0 auto', maxWidth: '100%' }} />;
}
